import * as React from "react";
import { Route, Redirect, Switch } from "react-router-dom";
import type { History, Location } from "history";

import { connect } from "react-redux";
import Grid from "./Grid";
import MainContent from "./MainContent";
import MainLeftMenu from "./MainLeftMenu/index";

import * as Auth from "../lib/Auth";
import * as BusinessLogic from "../lib/BusinessLogic";
import { USER_TAGS } from "../lib/CommonTags.generated";
import type { IPlantData, ILineGroupData } from "../lib/backend/manufacturing2.generated.types";
import local_settings, { FIELD_LAST_PLANT, FIELD_LAST_LINEGROUP } from "../lib/LocalSettings";

import { getRoutes as getSystemAdminRoutes } from "./sa";
import { getRoutes as getDigitalTwinRoutes } from "./dt";

import Header from "./Header";
import Home from "./Home";

import HomeView from "./manufacturing/HomeView";
import LinesView from "./manufacturing/LinesView";
import StocksView from "./manufacturing/StocksView";
import PeopleView from "./manufacturing/PeopleView";
import ToolsView from "./manufacturing/ToolsView";
import TicketingApp from "./ticketing/TicketingApp";

import InsightsPage from "./manufacturing/InsightsPage";

import BatchOperationsStandalone from "./manufacturing/BatchOperationsStandalone";
import BatchOperationsArchiveStandalone from "./manufacturing/BatchOperationsArchiveStandalone";
import KanbanStandalone from "./manufacturing/KanbanStandalone";
import ManufacturingAdmin from "./sa/components/ManufacturingAdmin";

import Aka from "./Aka";

import Settings from "./Settings";

import DashboardStandalone from "./DashboardStandalone";

import Oauth2Step2 from "./Oauth2Step2";
import Login from "./Login";
import LoginRefresh from "./LoginRefresh";
import RecoverPassword from "./RecoverPassword";
import ChangePasswordStandalone from "./ChangePasswordStandalone";
import ChangePasswordWithCookie from "./ChangePasswordWithCookie";
import TermsAndConditions from "./TermsAndConditions";
import PrivacyPolicy from "./PrivacyPolicy";
import Footer from "./Footer";

import { subscribe, MSG_TYPES } from "../lib/PubSub";
import { LINE_GROUP_TAGS } from "../lib/ManufacturingTags.generated";
import { configureDatadogRumIfNeeded } from "../lib/datadog/configureRum";

import ToolTransfer from "./manufacturing/ToolTransfer";

import DemandForecasting from "./DemandForecasting";

import * as URLRedirect from "./URLHandler/Redirect";
import TwoFAStandalone from "./TwoFAStandalone";
import ManualEntryOrders from "./manufacturing/ManualEntryOrders";
import PlanningTablePerformance from "./react/performance/PlanningTablePerformance";
import { PLANT_TAGS_ACCESS } from "../lib/ManufacturingTags.generated";
import KioskUndefined from "./manufacturing/KioskUndefined";

import * as Utils from "../lib/Util";

import type { ReduxState } from "./redux/reducers";

import ErrorComponent from "./ErrorComponent";
import { FormattedMessage } from "react-intl";
import { translate } from "./IntlProviderWrapper";

type OwnProps = {
    history: History;
    location: Location;
};

type Props = OwnProps & PropsFromRedux;

type State = {
    username: string;
    env_flag: string;
    show_manufacturing: boolean;
    // manufacturing terminal context
    plants: IPlantData[];
    plant_uuid: string;
    line_groups: ILineGroupData[];
    line_group_uuid: string;
    last_url: string;
};

// list of URL paths that do not require user to be registered
const NO_LOGIN_PATHS = [
    "/login",
    "/recover_password",
    "/terms",
    "/privacy",
    "/dashboard-public",
    "/auth/callback/github",
    "/auth/callback/azure-ad",
    "/auth/callback/external_provider",
    "/users/check-pwd-reset-with-cookie",
];

function noLoginRequired(path: string): boolean {
    if (path === "/") return false;
    return NO_LOGIN_PATHS.some(x => path.startsWith(x));
}

// list of URL paths that have fixed header
const FIXED_PATHS = [
    "/manufacturing",
    "/insights_page",
    "/resources",
    "/shifts",
    "/users",
    "/material_stock_forecast",
    "/kiosk/batch_operations",
    "/kiosk/batch_operations_archive",
    "/kiosk/kanban",
    "/kiosk/manual_entry_order",
    "/kiosk",
    "/events",
    "/datasources",
    "/dashboard",
    "/dashboards",
    "/demand-forecasting",
    "/background_jobs",
    "/long-term-stock-forecast",
    "/electricity",
];

export const MANUFACTURING_PREFIX = "/manufacturing";
export const DIGITAL_TWIN_PREFIX = "/digital-twin";
export const MANUFACTURING_SKIP_BACK = ["/manufacturing/admin", "/manufacturing/eval"];
export const ADMIN_PREFIX = "/admin";

function isFixedHeader(path: string): boolean {
    // for nested roots based on parameters
    // e.g. /alerts/12345
    const path_parts = path.split("/");
    if (path_parts.length > 1) {
        path = "/" + path_parts[1];
    }
    return FIXED_PATHS.includes(path);
}

// list of URL paths that have no header
const NO_HEADER_PATHS = ["/planning-table-performance"];

const NO_HEADER_PATHS_MULTIPART = ["/dashboard-public"];

function isNoHeader(path: string): boolean {
    // for nested roots based on parameters
    // e.g. /alerts/12345
    const path_parts = path.split("/");
    let was_multipart = false;
    if (path_parts.length > 1) {
        was_multipart = path_parts.length > 2;
        path = "/" + path_parts[1];
    }

    return NO_HEADER_PATHS.includes(path) || (NO_HEADER_PATHS_MULTIPART.includes(path) && was_multipart);
}

class App extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        // initialize authentification from the local storage
        const state: State = {
            username: "",
            env_flag: "",
            show_manufacturing: false,
            plants: [],
            plant_uuid: "",
            line_groups: [],
            line_group_uuid: "",
            last_url: "/manufacturing/home",
        };
        this.state = state;
        // if user was changed we need to reload the header and other stuff
        const handleUserChange = async () => {
            this.setState({ username: Auth.getLoggedinUser().username }, () => {
                this.load();
            });
        };
        subscribe(MSG_TYPES.logout, handleUserChange);
        subscribe(MSG_TYPES.login, handleUserChange);
        subscribe(MSG_TYPES.user_profile_reloaded, handleUserChange);
        subscribe(MSG_TYPES.manufacturing_data_updated, handleUserChange);
    }

    async componentDidMount() {
        this.load();
    }

    getLineGroups = (plant_uuid: string) => {
        return BusinessLogic.getLineGroupsForUserPlant(plant_uuid).filter(lg => lg.tags.hide_in_navigation !== "true");
    };

    shouldComponentUpdate(prevProps: Props, prevState: State) {
        if (
            JSON.stringify(this.state) === JSON.stringify(prevState) &&
            prevProps.history === this.props.history &&
            prevProps.location === this.props.location &&
            prevProps.persistent_filters === this.props.persistent_filters
        ) {
            return false;
        }

        return true;
    }

    async load() {
        await BusinessLogic.waitForManufacturingAsync();

        try {
            // collect environment parameters
            const env_flag = BusinessLogic.getSysFlag("env");
            const show_manufacturing = BusinessLogic.displayFeature("manufacturing");

            await BusinessLogic.loadAndSaveAppVersion();

            // filtered out plants that have no line groups;
            const plants: IPlantData[] = [];
            for (const plant of BusinessLogic.getPlantsForUser()) {
                const line_groups = this.getLineGroups(plant.uuid);

                if (line_groups.length === 0) continue;
                plants.push(plant);
            }

            if (plants.length === 0) {
                console.log("User has permission to access NO plants");
                return;
            }

            let plant_uuid = this.props.persistent_filters.plant_uuid;
            let plant_changed = false;
            // check if the user still has permision to see the plant
            if (plants.findIndex(p => p.uuid === plant_uuid) === -1) {
                const last_plant = plants.find(p => p.uuid === local_settings.getItem(FIELD_LAST_PLANT));
                plant_uuid = last_plant ? last_plant.uuid : plants[0].uuid;
                plant_changed = true;
            }

            const line_groups = this.getLineGroups(plant_uuid);
            if (line_groups.length === 0) {
                console.log("User has permission to access NO line groups in selected plant");
                return;
            }

            let line_group_uuid = this.props.persistent_filters.line_group_uuid;
            let line_group_changed = false;
            // check if the user still has permission to see the line group
            if (line_groups.findIndex(lg => lg.uuid === line_group_uuid) === -1) {
                const last_linegroup = line_groups.find(lg => lg.uuid === local_settings.getItem(FIELD_LAST_LINEGROUP));
                line_group_uuid = last_linegroup ? last_linegroup.uuid : line_groups[0].uuid;
                line_group_changed = true;
            }

            const plant_tags = BusinessLogic.getPlantTags(plant_uuid);
            const enable_home = PLANT_TAGS_ACCESS.new_feature_home_button(plant_tags);

            let last_url = this.state.last_url;

            if (!enable_home && last_url.includes("/manufacturing/home")) {
                last_url = "/manufacturing/lines";
            }

            this.setState(
                {
                    env_flag,
                    show_manufacturing,
                    plants,
                    line_groups,
                    last_url,
                },
                () => {
                    if (plant_changed || line_group_changed) {
                        URLRedirect.persistentFilters(
                            this.props.history,
                            this.props.location.pathname + this.props.location.search,
                            { plant_uuid, line_group_uuid }
                        );
                    } else {
                        this.returnLocation(this.props.location.pathname + this.props.location.search);
                    }
                }
            );
        } catch (err: unknown) {
            console.log(err);
        }
    }

    setPlant = (plant_uuid: string) => {
        // get list of linegroups for the plant, ignoring ones that are marked as not to show in navigation bar
        const line_groups = BusinessLogic.getLineGroupsForUserPlant(plant_uuid).filter(
            line_group =>
                line_group.tags[LINE_GROUP_TAGS.hide_in_navigation] === undefined ||
                line_group.tags[LINE_GROUP_TAGS.hide_in_navigation] === "false"
        );
        // make sure we have something
        if (line_groups.length === 0) {
            console.log("User has permission to access NO line groups in selected plant");
            return;
        }
        const line_group_uuid = line_groups[0].uuid;
        const is_on_planning_table = window.location.pathname.includes("/planning_table");
        if (is_on_planning_table) {
            local_settings.setItem(FIELD_LAST_PLANT, plant_uuid);
            local_settings.setItem(FIELD_LAST_LINEGROUP, line_group_uuid);
            window.location.hash = "";
            window.location.reload();
        } else {
            URLRedirect.persistentFilters(this.props.history, this.props.location.pathname, {
                plant_uuid,
                line_group_uuid,
            });
            this.setState({ line_groups });
        }
    };

    setLineGroup = (line_group_uuid: string) => {
        const is_on_planning_table = window.location.pathname.includes("/planning_table");
        if (is_on_planning_table) {
            local_settings.setItem(FIELD_LAST_LINEGROUP, line_group_uuid);
            window.location.hash = "";
            window.location.reload();
        } else {
            URLRedirect.persistentFilters(this.props.history, this.props.location.pathname, {
                line_group_uuid,
                plant_uuid: this.props.persistent_filters.plant_uuid,
            });
        }
    };

    async configureDataDogRUM() {
        await configureDatadogRumIfNeeded();
    }

    async componentDidUpdate(prev_props: Props, prev_state: State) {
        const user = Auth.getLoggedinUser();
        if (user.uuid.length > 0) {
            await this.configureDataDogRUM();
        }

        if (prev_state.username !== this.state.username && this.state.username) {
            await BusinessLogic.forceManufacturingCacheRefresh();
            return this.load();
        }
        const path = this.props.location.pathname;
        if (prev_props.location.pathname !== path) {
            this.returnLocation(path);
        }
    }

    returnLocation(path: string) {
        // handle users returning to manufacturing from other parts
        if (path.startsWith(MANUFACTURING_PREFIX)) {
            if (path === MANUFACTURING_PREFIX) {
                // no specific target, check if we have last position
                if (this.state.last_url.length > MANUFACTURING_PREFIX.length) {
                    this.props.history.push(this.state.last_url);
                }
            } else if (MANUFACTURING_SKIP_BACK.every(x => x !== path)) {
                // we have a specific target, which is not on skip list
                this.setState({ last_url: path });
            }
        }
    }

    // sets grid-layout flag in session storage
    // it is used for navigation bar in whole app
    setGridLayoutLocalStorage = (): void => {
        const path = this.props.location.pathname;
        if (path.includes("/admin") || path.includes("/digital-twin")) {
            Utils.setGridLayoutSettings(true);
        } else {
            Utils.setGridLayoutSettings(false);
        }
    };

    handleLogout() {
        Auth.logout();
        return <Redirect to="/" />;
    }

    redirectToFirstRoute(routes: { [key: string]: boolean }) {
        let home_enabled = false;
        // If we don't have home enabled, we shouldn't redirect to it
        if (this.state.plant_uuid) {
            const plant_tags = BusinessLogic.getPlantTags(this.state.plant_uuid);
            if (PLANT_TAGS_ACCESS.new_feature_home_button(plant_tags)) {
                home_enabled = true;
            }
        }
        if (!home_enabled) {
            routes.home = false;
        }
        const first_route = Object.keys(routes).find(route => routes[route]);
        if (first_route) {
            return <Redirect to={`/manufacturing/${first_route}`} />;
        }
        return <Redirect to="/" />;
    }

    showNoPermissionsLogout() {
        return (
            <div className="text-center mx-auto p-3 w-50">
                <ErrorComponent
                    msg={translate(
                        "common.missing_permissions",
                        "You do not have permission to access Production Guiding, Digital Twin or Ticketing modules."
                    )}
                    type="error"
                    onlyDiv
                    is_closable={false}
                />
                <button className="btn btn-primary" onClick={this.handleLogout}>
                    <FormattedMessage id="Header.menu.logout" defaultMessage="Log out" />
                </button>
            </div>
        );
    }

    render() {
        // get current path
        const path: string = this.props.location.pathname;
        const show_header = !isNoHeader(path);

        // check if we have a kiosk user
        const is_kiosk = Auth.getUserTag(USER_TAGS.kiosk) === "true";
        // if we do, also check if we need a redirect
        if (is_kiosk) {
            const kiosk_screen = Auth.getUserTag(USER_TAGS.kiosk_screen);
            const kiosk_url = `kiosk/${kiosk_screen !== null ? kiosk_screen : "not_defined"}`;
            // kiosk can only watch dedicated screens or public dashboards
            if (!path.includes(kiosk_url) && !path.includes("dashboard-public")) {
                return <Redirect to={`/${kiosk_url}`} />;
            }
        }
        let component: React.ReactNode = null;

        // recalculate grid layout flag
        this.setGridLayoutLocalStorage();

        const is_grid_layout = Utils.getGridLayoutSettings();

        // check if we are logged in or are we asking for an open page
        if (Auth.isAuth()) {
            const path_is_settings = path.includes("/settings");
            // Check additional permissions for internal routes
            const routePermissions = {
                home: Auth.hasPermission(Auth.PERMISSION_NAMES.HomeView),
                lines: Auth.hasPermission(Auth.PERMISSION_NAMES.LinesView),
                stocks: Auth.hasPermission(Auth.PERMISSION_NAMES.StocksView),
                people: Auth.hasPermission(Auth.PERMISSION_NAMES.PeopleView),
                tools: Auth.hasPermission(Auth.PERMISSION_NAMES.ToolView),
            };

            // Get main permissions
            const can_access_pg = Auth.hasPermission(Auth.PERMISSION_NAMES.ProductionGuiding);
            const can_access_dt = Auth.hasPermission(Auth.PERMISSION_NAMES.DigitalTwin);
            const can_access_ticketing = Auth.hasPermission(Auth.PERMISSION_NAMES.Ticketing);

            // Everyone should have access to settings
            // Kiosk is handled separately
            if (!path_is_settings && (!is_kiosk || !path.includes("kiosk"))) {
                // If user lacks all primary permissions, log out
                if (!can_access_pg && !can_access_dt && !can_access_ticketing) {
                    return this.showNoPermissionsLogout();
                }

                // Redirect to digital twin if appropriate
                if (!can_access_pg && can_access_dt && (path.includes(MANUFACTURING_PREFIX) || path === "/")) {
                    return <Redirect to="/digital-twin/resources/plants" />;
                }

                // Redirect to ticketing if only ticketing is available
                if (!can_access_dt && !can_access_pg && can_access_ticketing && !path.includes("/ticketing")) {
                    return <Redirect to="/ticketing" />;
                }

                // If no route permissions exist (and no DT or ticketing), log out
                if (
                    Object.values(routePermissions).every(hasPerm => !hasPerm) &&
                    !can_access_ticketing &&
                    !can_access_dt
                ) {
                    return this.showNoPermissionsLogout();
                }

                // If on a digital twin route without DT permission but with ticketing, redirect to ticketing
                if (!can_access_dt && can_access_ticketing && path.includes(DIGITAL_TWIN_PREFIX)) {
                    return <Redirect to="/ticketing" />;
                }

                // If only production guiding is allowed and the current path isn’t manufacturing, redirect accordingly
                if (can_access_pg && !can_access_dt && !can_access_ticketing && !path.includes(MANUFACTURING_PREFIX)) {
                    return this.redirectToFirstRoute(routePermissions);
                }
            }

            // user is logged in
            component = (
                <Grid>
                    <Header
                        history={this.props.history}
                        location={this.props.location}
                        show={show_header}
                        fixed={isFixedHeader(path)}
                        username={this.state.username}
                        env_flag={this.state.env_flag}
                        show_manufacturing={this.state.show_manufacturing}
                        plants={this.state.plants}
                        plant_uuid={this.props.persistent_filters.plant_uuid}
                        set_plant_uuid={this.setPlant}
                        line_groups={this.state.line_groups}
                        line_group_uuid={this.props.persistent_filters.line_group_uuid}
                        set_line_group_uuid={this.setLineGroup}
                        key="header"
                    />
                    {is_grid_layout && <MainLeftMenu history={this.props.history} />}
                    <MainContent>
                        <Switch>
                            <Route path="/" exact={true} component={Home} key="0" />

                            {/*manufacturing */}
                            <Route
                                path="/manufacturing/home/:selected_view?/:view_params?"
                                exact={true}
                                key="1004"
                                render={props => {
                                    // We don't have permission to access the selected view
                                    if (routePermissions.home === false) {
                                        // At least one route is accessible for the user - earlier check
                                        return this.redirectToFirstRoute(routePermissions);
                                    }
                                    return (
                                        <HomeView
                                            {...props}
                                            plant_uuid={this.props.persistent_filters.plant_uuid}
                                            line_group_uuid={this.props.persistent_filters.line_group_uuid}
                                        />
                                    );
                                }}
                            />
                            <Route
                                path="/manufacturing/lines/:selected_view?/:view_params?"
                                exact={true}
                                key="1000"
                                render={props => {
                                    // We don't have permission to access the selected view
                                    if (routePermissions.lines === false) {
                                        // At least one route is accessible for the user - earlier check
                                        return this.redirectToFirstRoute(routePermissions);
                                    }
                                    return (
                                        <LinesView
                                            {...props}
                                            plant_uuid={this.props.persistent_filters.plant_uuid}
                                            line_group_uuid={this.props.persistent_filters.line_group_uuid}
                                        />
                                    );
                                }}
                            />
                            <Route
                                path="/manufacturing/stocks/:selected_view?"
                                exact={true}
                                key="1001"
                                render={props => {
                                    // We don't have permission to access the selected view
                                    if (routePermissions.stocks === false) {
                                        // At least one route is accessible for the user - earlier check
                                        return this.redirectToFirstRoute(routePermissions);
                                    }

                                    return (
                                        <StocksView
                                            {...props}
                                            plant_uuid={this.props.persistent_filters.plant_uuid}
                                            line_group_uuid={this.props.persistent_filters.line_group_uuid}
                                        />
                                    );
                                }}
                            />
                            <Route
                                path="/manufacturing/people/:selected_view?/:view_params?"
                                exact={true}
                                key="1002"
                                render={props => {
                                    // We don't have permission to access the selected view
                                    if (routePermissions.people === false) {
                                        // At least one route is accessible for the user - earlier check
                                        return this.redirectToFirstRoute(routePermissions);
                                    }

                                    return (
                                        <PeopleView
                                            {...props}
                                            plant_uuid={this.props.persistent_filters.plant_uuid}
                                            line_group_uuid={this.props.persistent_filters.line_group_uuid}
                                        />
                                    );
                                }}
                            />
                            <Route
                                path="/manufacturing/tools/:selected_view?"
                                exact={true}
                                key="1003"
                                render={props => {
                                    // We don't have permission to access the selected view
                                    if (routePermissions.tools === false) {
                                        // At least one route is accessible for the user - earlier check
                                        return this.redirectToFirstRoute(routePermissions);
                                    }
                                    return (
                                        <ToolsView
                                            {...props}
                                            plant_uuid={this.props.persistent_filters.plant_uuid}
                                            line_group_uuid={this.props.persistent_filters.line_group_uuid}
                                        />
                                    );
                                }}
                            />
                            <Route path="/insights_page" component={InsightsPage} key="131" />

                            {/* manufacturing batch operations kiosk mode */}
                            <Route
                                path="/kiosk/batch_operations/:group_uuid?"
                                component={BatchOperationsStandalone}
                                key="600"
                            />
                            <Route
                                path="/kiosk/batch_operations_archive/:group_uuid"
                                component={BatchOperationsArchiveStandalone}
                                key="602"
                            />
                            <Route path="/kiosk/kanban" component={KanbanStandalone} key="604" />
                            <Route path="/kiosk/ticketing" component={TicketingApp} key="605" />
                            <Route
                                path="/kiosk/manual_entry_order"
                                render={() => <ManualEntryOrders is_kiosk={true} line_group_uuid={""} />}
                                key="606"
                            />
                            <Route path="/kiosk/undefined" component={KioskUndefined} key="607" />
                            {/* <Route path="/ticketing" component={TicketingApp} key="608" /> */}
                            <Route
                                path="/ticketing"
                                key="608"
                                render={props => {
                                    if (!can_access_ticketing) {
                                        return <Redirect to="/manufacturing/home" />;
                                    }
                                    return <TicketingApp {...props} />;
                                }}
                            />

                            {/* manufacturing admin */}
                            <Route path="/manufacturing/admin" component={ManufacturingAdmin} key="110" />
                            <Route path="/aka" component={Aka} key="500" />

                            {/* datasources */}
                            <Route path="/settings" component={Settings} key="204" />

                            {/* dashboards */}
                            <Route
                                path="/dashboard/:uuid"
                                key="14.0"
                                render={routeProps => <DashboardStandalone {...routeProps} show_filter_bar={true} />}
                            />
                            <Route
                                path="/dashboard-public/:uuid"
                                key="14.1"
                                render={routeProps => <DashboardStandalone {...routeProps} show_filter_bar={true} />}
                            />

                            {/* demand forecasting */}
                            <Route
                                path="/demand-forecasting/:plant_external_id?/:material_external_id?"
                                component={DemandForecasting}
                                key="650"
                            />

                            {/* misc */}
                            <Route path="/change_password" component={ChangePasswordStandalone} key="700" />
                            <Route
                                path="/manufacturing/tool_transfer/:order_external_id/:process_num"
                                component={ToolTransfer}
                                key="701"
                            />
                            <Route path="/activate_twofa" component={TwoFAStandalone} key="702" />

                            {/* admin */}
                            <Route path="/admin" render={getSystemAdminRoutes} />
                            <Route path="/planning-table-performance" component={PlanningTablePerformance} key="623" />
                            <Route path="/digital-twin" render={getDigitalTwinRoutes} />

                            <Route path="/login" component={LoginRefresh} key="5" />
                        </Switch>
                    </MainContent>
                </Grid>
            );
        } else if (noLoginRequired(path)) {
            // user is not logged in
            component = [
                <div id="wrapper" key="wrapper">
                    <Route
                        path="/dashboard-public/:uuid"
                        render={routeProps => (
                            <DashboardStandalone {...routeProps} asPublic={true} show_filter_bar={true} />
                        )}
                        key="14"
                    />
                    <Route path="/auth/callback/:type" component={Oauth2Step2} key="4" />
                    <Route path="/login" component={Login} key="5" />
                    <Route path="/recover_password" component={RecoverPassword} key="7" />
                    <Route path="/terms" component={TermsAndConditions} key="8" />
                    <Route path="/privacy" component={PrivacyPolicy} key="9" />
                    <Route path="/users/check-pwd-reset-with-cookie" component={ChangePasswordWithCookie} key="10" />
                    <Footer position="fixed" key="footer" />
                </div>,
            ];
        } else {
            // we are not logged in and are asking for page that requires login
            component = <Redirect to="/login" />;
        }

        return <React.Fragment>{component}</React.Fragment>;
    }
}

const mapStateToProps = (state: ReduxState) => ({
    persistent_filters: state.persistent_filters,
    stock_status_filters: state.stock_status_filters,
    translations_loading: state.translations_materials.is_loading,
});

type PropsFromRedux = ReturnType<typeof mapStateToProps>;

export default connect<PropsFromRedux, {}, OwnProps, ReduxState>(mapStateToProps)(App);
